import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MessageCircle, Download, Check } from "lucide-react";
import { MaskText, Reveal } from "@/components/motion";
import { Footer } from "@/components/Footer";
import { CalendarStep, VideoStep, QuestionStep, PassStep } from "@/components/thankyou/PrepSteps";
import { http, getConfig } from "@/lib/api";
import { track } from "@/lib/tracking";
import { LOGO, MEDIA, EVENT_TIME } from "@/lib/content";

const usePayment = (sessionId) => {
  const [info, setInfo] = useState(null);
  const [state, setState] = useState(sessionId ? "checking" : "missing");
  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await http.get(`/payments/status/${sessionId}`);
        if (r.data?.payment_status === "paid") {
          setInfo(r.data);
          setState("paid");
          return;
        }
      } catch {
        /* retry */
      }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);
  return { info, state };
};

const firstName = (n) => (n || "").trim().split(/\s+/)[0] || "";

const Hero = ({ name, confirmed }) => (
  <header className="relative overflow-hidden px-5 pb-16 pt-8 sm:px-8" data-testid="ty-hero">
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(199,154,75,0.22),transparent_70%)]" />
    <div className="relative mx-auto max-w-3xl text-center">
      <Link to="/"><img src={LOGO} alt="Skin Lovers Professional" className="mx-auto h-5 w-auto sm:h-6" /></Link>
      <MaskText as="h1" onLoad delay={0.2} key={confirmed ? "c" : "p"} className="mt-14 text-5xl leading-[1] text-cream sm:text-6xl lg:text-7xl" lines={[confirmed ? <span className="gold-text">Sei dentro, {name}!</span> : <>Ci sei quasi, <span className="gold-text">{name}.</span></>]} data-testid="ty-title" />
      <Reveal><p className="lead mx-auto mt-6 max-w-xl">Pagamento ricevuto: il tuo posto al workshop Natural Anti-Age del 25 e 26 novembre è riservato. La fattura arriva via email. Manca un ultimo passaggio per ricevere il link Zoom.</p></Reveal>
      <div className="mx-auto mt-10 max-w-md" data-testid="ty-progress">
        <div className="h-1.5 overflow-hidden rounded-full bg-line">
          <motion.div className="h-full bg-gradient-to-r from-gold-dark via-gold to-gold-light" initial={{ width: "0%" }} animate={{ width: confirmed ? "100%" : "50%" }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.6 }} />
        </div>
        <p className="mt-3 flex justify-between text-xs text-sand">
          <span>Pagamento ricevuto ✓</span>
          <b className="text-gold-light">{confirmed ? "Iscrizione completa" : "Manca: conferma su WhatsApp"}</b>
        </p>
      </div>
    </div>
  </header>
);

const WhatsAppBox = ({ info, confirmed, onConfirm }) => {
  const [num, setNum] = useState("");
  useEffect(() => {
    getConfig().then((c) => setNum((c.whatsapp_number || "").replace(/\D/g, "")));
  }, []);
  const text = `Ciao! Confermo la mia presenza al workshop Natural Anti-Age del 25 e 26 novembre. ${info.nome || ""} - ${info.nome_centro || ""}`;
  const href = `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
  return (
    <Reveal className="px-5 sm:px-8">
      <div className="mx-auto max-w-3xl rounded-3xl border border-gold/50 bg-[radial-gradient(100%_100%_at_50%_0%,rgba(199,154,75,0.18),#120e08_70%)] p-8 text-center sm:p-12" data-testid="ty-whatsapp-box">
        <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">Passaggio obbligatorio</span>
        <h2 className="mt-4 text-4xl text-cream sm:text-5xl">Conferma la tua presenza su WhatsApp</h2>
        <p className="mx-auto mt-4 max-w-lg text-[15px] leading-relaxed text-sand">Il link Zoom e i promemoria arrivano solo su WhatsApp. Tocca il pulsante: si apre una chat con il messaggio già scritto, devi solo premere invio.</p>
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={onConfirm} className="btn-gold mt-8" data-testid="ty-whatsapp-btn">
          {confirmed ? <Check className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
          {confirmed ? "Confermato ✓" : "Conferma su WhatsApp"}
        </a>
        <small className="mt-4 block text-xs text-sand">Senza conferma, il posto torna disponibile per un'altra titolare.</small>
      </div>
    </Reveal>
  );
};

const Bonus = () => (
  <section className="px-5 py-20 sm:px-8" data-testid="ty-bonus">
    <div className="mx-auto max-w-5xl">
      <Reveal><span className="eyebrow">I tuoi bonus</span></Reveal>
      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <Reveal className="h-full"><div className="card-lux h-full p-8">
          <h3 className="text-3xl text-cream">PDF "Protocollo viso Super Glow"</h3>
          <p className="mt-3 text-sm leading-relaxed text-sand">Il protocollo veloce da fare subito in cabina. Scaricalo ora, te lo mandiamo anche su WhatsApp.</p>
          {MEDIA.superGlowPdf ? (
            <a href={MEDIA.superGlowPdf} download className="btn-gold mt-6 !py-3" data-testid="ty-pdf-download-btn"><Download className="h-4 w-4" /> Scarica il PDF</a>
          ) : (
            <button type="button" disabled className="btn-gold mt-6 !py-3" data-testid="ty-pdf-download-btn"><Download className="h-4 w-4" /> PDF disponibile a breve</button>
          )}
        </div></Reveal>
        <Reveal delay={0.1} className="h-full"><div className="card-lux h-full p-8">
          <h3 className="text-3xl text-cream">Replay per 30 giorni</h3>
          <p className="mt-3 text-sm leading-relaxed text-sand">Dopo ogni serata ricevi il link per riguardarla, valido 30 giorni. Puoi farla vedere anche al tuo staff.</p>
          <span className="chip mt-6">Disponibile dal 25 novembre</span>
        </div></Reveal>
      </div>
    </div>
  </section>
);

const Summary = () => (
  <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-3" data-testid="ty-summary">
    {[["Quando", "25 e 26 novembre", "mercoledì e giovedì"], ["Orario", EVENT_TIME, "ora italiana"], ["Dove", "Zoom", "link su WhatsApp dopo la verifica"]].map(([k, v, s]) => (
      <div key={k} className="bg-ink2 p-6">
        <small className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">{k}</small>
        <b className="mt-2 block font-serif text-3xl font-semibold text-cream">{v}</b>
        <span className="text-sm text-sand">{s}</span>
      </div>
    ))}
  </div>
);

const Status = ({ state }) => (
  <main className="flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="ty-status">
    <img src={LOGO} alt="Skin Lovers Professional" className="h-6 w-auto" />
    {state === "checking" ? (
      <p className="mt-10 flex items-center gap-3 text-sand"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Stiamo verificando il pagamento…</p>
    ) : (
      <>
        <h1 className="mt-10 text-4xl text-cream">{state === "pending" ? "Pagamento in verifica" : "Nessun pagamento trovato"}</h1>
        <p className="mt-4 max-w-md text-sand">{state === "pending" ? "Non abbiamo ancora la conferma da Stripe. Ricarica la pagina tra qualche minuto: se hai pagato, riceverai comunque la conferma su WhatsApp ed email." : "Per accedere a questa pagina completa prima l'iscrizione al workshop."}</p>
        <Link to="/#iscrizione" className="btn-gold mt-8" data-testid="ty-back-to-form">Vai all'iscrizione</Link>
      </>
    )}
  </main>
);

export default function ThankYou() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { info, state } = usePayment(sessionId);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (state !== "paid") return;
    const k = `sl_purchase_${sessionId}`;
    if (!localStorage.getItem(k)) {
      localStorage.setItem(k, "1");
      track("Purchase", { value: 32.94, currency: "EUR" });
      sessionStorage.removeItem("sl_registration");
    }
  }, [state, sessionId]);

  if (state !== "paid") return <Status state={state} />;
  const name = firstName(info.nome);

  return (
    <main data-testid="thankyou-page">
      <Hero name={name} confirmed={confirmed} />
      <WhatsAppBox info={info} confirmed={confirmed} onConfirm={() => setConfirmed(true)} />
      <Bonus />
      <section className="border-t border-line bg-ink2 px-5 py-20 sm:px-8">
        <div className="mx-auto max-w-5xl">
          <Reveal><span className="eyebrow">Nel frattempo</span></Reveal>
          <MaskText className="h2 mt-6" lines={["Preparati alle", <em className="gold-text">2 serate</em>]} />
          <div className="mt-6">
            <CalendarStep />
            <VideoStep />
            <QuestionStep sessionId={sessionId} />
            <PassStep info={info} />
          </div>
          <Summary />
        </div>
      </section>
      <Footer />
    </main>
  );
}
